import React from "react";
import { BsPatchCheck } from "react-icons/bs";
import { IoWarningOutline } from "react-icons/io5";
import { BsFileEarmarkArrowDown } from "react-icons/bs";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { getNairaFormat } from "../../utils/Utils";
import { getFormattedDate, getFormattedDateALT } from "../../utils/DateUtils";
import DOMPurify from "dompurify";

/* eslint-disable react/prop-types */
const FormCurrencyFields = [
	"amount",
	"amountToInvest",
	"amountToRaise",
	"programSavings",
	"expectedProfit",
	"DORamount",
	"inspectionFee",
	"maintenanceFee",
	"DORequityContribution",
	"repairFee",
	"deliveryFee",
];
const FormPercentageFields = ["interestRate", "interest", "expectedProfitRate"];

export const InputField = ({
	name,
	handleChange,
	value,
	isEditable,
	type,
	maxLength,
	required,
	placeholder,
	min,
	max,
	minLength,
}) => {
	const isCurrency = FormCurrencyFields.includes(name);
	const isPercentage = FormPercentageFields.includes(name);
	const getValue = (value) => {
		if (isCurrency) {
			return getNairaFormat(value);
		}
		if (isPercentage) {
			const hasPercentage = value.toString().includes("%");
			return hasPercentage ? value : value + "%";
		}
		return value;
	};

	return isEditable ? (
		<input
			value={value || ""}
			className='edit'
			name={name}
			onChange={handleChange}
			maxLength={maxLength}
			type={type}
			min={min}
			max={max}
			minLength={minLength}
			placeholder={placeholder}
			required={required}
			readOnly={!handleChange && !name}
		/>
	) : (
		<span className='inputText'>{value ? getValue(value) : "---"}</span>
	);
};

export const TextAreaField = ({ name, handleChange, value, isEditable, placeholder, required }) => {
	return isEditable ? (
		<textarea
			value={value || ""}
			className='edit'
			name={name}
			onChange={handleChange}
			rows='5'
			required={required}
			placeholder={placeholder}
			style={{ width: "100%", border: "1px solid  #999999" }}
		/>
	) : (
		// <span className='text_view'>{value ? value : "N/A"}</span>
		<span
			className='text_view'
			dangerouslySetInnerHTML={{ __html: value ? DOMPurify.sanitize(value) : "N/A" }}></span>
	);
};

export const SelectField = ({
	name,
	handleChange,
	value,
	options = [],
	isEditable,
	placeholder,
	required,
}) => {
	// console.log(value);
	return isEditable ? (
		<select className='edit' name={name} onChange={handleChange} value={value} required={required}>
			<option value=''>{placeholder ? placeholder : "--- Select ---"}</option>
			{options.map((option) => (
				<option
					key={option?.id || option?.name || option?.department || option}
					value={option?.id || option?.name || option?.department || option}>
					{option?.name || option?.department || option || null}
				</option>
			))}
		</select>
	) : (
		<div className='inputText'>
			<span style={{ textTransform: "capitalize" }}>{value ? value : "N/A"}</span>
		</div>
	);
};

export const SelectFieldStatus = ({ name, handleChange, value, options = [], type, isEditable }) => {
	// let type_class = type ? type?.toLowerCase()?.replace(/\s/g, "") : "";
	let type_class = type ? type : "";
	// console.log(type);
	return isEditable ? (
		<select className='edit' name={name} onChange={handleChange} value={value}>
			<option value=''>--- Select ---</option>
			{options.map((option) => (
				<option key={option} value={option}>
					{option}
				</option>
			))}
		</select>
	) : (
		<div className={"selectBadge " + type_class}>
			<span>{value ? value : "N/A"}</span>
			{
				// type: approved, pending: NULL, rejected
				type == "approved" ? (
					<BsPatchCheck className='statusIcon' />
				) : type == "rejected" ? (
					<IoWarningOutline className='statusIcon' />
				) : null
			}
		</div>
	);
};

// FileGroup
export const FileGroup = ({ value }) => {
	return (
		<div className={"fileGroup flex gap-4" + (value ? "" : " opacity-50")}>
			<a className='viewFileBtn' href={value} download target='_blank' rel='noreferrer'>
				<BsFileEarmarkArrowDown className='fileIcon' />
				<span>Download</span>
			</a>
			<a className='side_btn' href={value} target='_blank' rel='noreferrer'>
				View
			</a>
		</div>
	);
};

// PasswordField
export const PasswordField = ({ name, value, handleChange, required, context }) => {
	// Password show/hide
	const [showPassword, setShowPassword] = React.useState(false);
	const togglePassword = () => setShowPassword(!showPassword);

	return (
		<div className='passwordField'>
			<input
				value={value || ""}
				className='edit'
				name={name}
				onChange={handleChange}
				type={showPassword ? "text" : "password"}
				required={required}
				maxLength={context === "pin" ? 4 : 20}
				inputMode={context === "pin" ? "numeric" : "text"}
			/>
			<span className='input_icon' onClick={togglePassword}>
				{showPassword ? <FaRegEye /> : <FaRegEyeSlash />}
			</span>
		</div>
	);
};

// Custom SelectField
export const CustomSelectField = ({ name, handleChange, value, options = [], isEditable }) => {
	// console.log(value,options )
	return isEditable ? (
		<select className='edit' name={name} onChange={handleChange} value={value}>
			<option value='null'>--- Select ---</option>
			{options.map((option) => (
				<option key={option.name || option} value={option.name || option}>
					{option.name || option || null}
				</option>
			))}
		</select>
	) : (
		<div className='inputText text_view'>
			<span>{value ? value : "N/A"}</span>
		</div>
	);
};
// isDateDisabled
export const CustomDateField = ({ name, handleChange, value, isEditable }) => {
	return isEditable ? (
		<input value={value || ""} className='edit' name={name} onChange={handleChange} type='datetime-local' />
	) : (
		<span className='inputText'>{value ? getFormattedDate(value) : "---"}</span>
	);
};

// Time Input Field
export const DateTimeInputField = ({ name, handleChange, value, isEditable, type }) => {
	const inputType = type || "datetime-local";
	const formattedValue = inputType == "datetime-local" ? getFormattedDate(value) : getFormattedDateALT(value);

	return isEditable ? (
		<input value={value || ""} className='edit' name={name} onChange={handleChange} type={inputType} />
	) : (
		<span className='inputText'>{value ? formattedValue : "---"}</span>
	);
};

export const PaymentStatusField = ({ status }) => (
	<p
		className='inputText'
		style={{ color: status === "Completed" ? "green" : status === "Incomplete" ? "red" : "black" }}>
		{status || "---"}
	</p>
);
